import { Component, Input, OnInit, OnDestroy, Output, ViewChild } from '@angular/core';
import { Subscription } from 'rxjs';
import { SignaturePad } from 'angular2-signaturepad';
import { ActivatedRoute, ParamMap } from '@angular/router';
import { PetService } from '../../../services/pet.service';
import { EstimateService, EstTranGroupResultModel } from '../../../services/estimate.service';
import { CatalogService } from '../../../services/catalog.service';
import { AddCodesFileModel, EstimateModel, ItemByCategoryModel } from '../../../models/estimate.model';
import { EstGroupsListModel } from '../../../models/est-groups.model';
import Swal from 'sweetalert2';
import { CodesFileModel } from '../../../models/codes-file.model';
import { SetupService } from '../../../services/setup.service';
import { ClientService } from '../../../services/client.service';
import { ProcedureService } from '../../../services/procedure.service';
import { CategoryModel } from '../../../models/category.model';
import { EstimateDefModel } from '../../../models/estimate.model';
import { DeleteATranModel } from "../../../models/delete-a-tran.model";
import { TranRecordModel } from '../../../models/tran-record.model';
import { INVENT } from '../../../../environments/environment';
import { CheckPetSexSpecieModel } from '../../../models/check-pet-sex-specie.model';
import { encode_base64, encode_utf8 } from '../../../../environments/environment';
import { SharedService } from '../../../services/shared.service';
import { EmailService } from '../../../services/email.service';
import { NgForm } from '@angular/forms';
import { PetProcsModel } from '../../../models/pet-proc.model';
import { EventEmitter } from '@angular/core';

declare var $: any;

@Component({
  selector: 'ngx-estimate-page',
  templateUrl: './estimate-page.component.html',
})
export class EstimatePageComponent implements OnInit, OnDestroy {
  private _subs = new Subscription();
  private _pet_no: number;
  @Input() set pet_no(value: number) {
    this._pet_no = value;

    this.filteredTable = [];
  }
  get pet_no(): number {
    return this._pet_no;
  }

  suggestedWordsArray: CodesFileModel[] = [];
  buscador_activo: boolean = false;
  filteredTable: PetProcsModel[];
  isLoading: boolean = false;
  colors = ['#FFADAD', '#FFDA65', '#FDFFB6', '#E4F1EE', '#D9EDF8', '#DEDAF4']
  selectedGroup: EstGroupsListModel;
  selectedItems = new Set<number>();
  showSelectMode: boolean = false;

  now: Date = new Date();
  signaturePadOptions: Object = {
    minWidth: 2
  };
  @ViewChild('signaturePadClient') signaturePadClient!: SignaturePad;
  @ViewChild('signaturePadAttending') signaturePadAttending!: SignaturePad;

  toggleSelectMode() {
    this.showSelectMode = !this.showSelectMode;
    if (!this.showSelectMode) {
      this.selectedItems.clear();
    }
  }

  toggleItemSelection(item: PetProcsModel) {
    if (this.selectedItems.has(item.tran_sequence)) {
      this.selectedItems.delete(item.tran_sequence);
    } else {
      this.selectedItems.add(item.tran_sequence);
    }
  }

  get isAllSelected(): boolean {
    const items = this.filteredTable || [];
    return items.length > 0 && items.every(item => this.selectedItems.has(item.tran_sequence));
  }

  toggleSelectAll() {
    if (this.isAllSelected) {
      this.selectedItems.clear();
    } else {
      (this.filteredTable || []).forEach(item => this.selectedItems.add(item.tran_sequence));
    }
  }

  assignSelectedToGroup(group: EstGroupsListModel) {
    if (this.selectedItems.size === 0) return;
    this.assignTranSequencesToGroup(Array.from(this.selectedItems), group, true);
  }

  private assignTranSequencesToGroup(tran_sequence: number[], group: EstGroupsListModel, updateSelectedItems: boolean) {
    if (tran_sequence.length === 0) return;

    this.estimateService.SetupEstTranGroup(group.ID, tran_sequence).then((results: EstTranGroupResultModel[]) => {
      const failed = results.filter(r => !r.success);
      const succeeded = results.filter(r => r.success);

      if (updateSelectedItems) {
        succeeded.forEach(r => this.selectedItems.delete(r.tran_sequence));
      }

      if (failed.length === 0) {
        Swal.fire({ title: `Added to "${group.description}"` });
        return;
      }

      const failedList = failed.map(r => `Transaction ${r.tran_sequence}: ${r.message || 'Unknown error'}`).join('<br>');

      if (succeeded.length === 0) {
        Swal.fire({
          title: 'Failed to add transactions to group',
          html: failedList,
          icon: 'error'
        });
      } else {
        Swal.fire({
          title: `Added ${succeeded.length} of ${results.length} to "${group.description}"`,
          html: failedList,
          icon: 'warning'
        });
      }
    }).catch((err) => {
      const msg = err?.error?.message || err?.message || 'Unable to add transactions to group.';
      Swal.fire('Error', msg, 'error');
    });
  }

  get filteredTotal(): number {
    return (this.filteredTable || []).reduce((sum, item) => sum + this.getRowTotalCost(item), 0);
  }

  get selectedItemsList(): PetProcsModel[] {
    return (this.filteredTable || []).filter(item => this.selectedItems.has(item.tran_sequence));
  }

  get selectedTotal(): number {
    return this.selectedItemsList.reduce((sum, item) => sum + this.getRowTotalCost(item), 0);
  }

  get selectedTotalQty(): number {
    return this.selectedItemsList.reduce((sum, item) => sum + (parseFloat(item.unit) || 0), 0);
  }

  // Falls back to the whole list (e.g. a Collection) when nothing is individually checked,
  // but only outside Select Mode — in Select Mode an actual selection is required.
  get approvalItemsList(): PetProcsModel[] {
    if (this.showSelectMode) {
      return this.selectedItemsList;
    }
    return this.selectedItems.size > 0 ? this.selectedItemsList : (this.filteredTable || []);
  }

  get approvalTotal(): number {
    if (this.showSelectMode) {
      return this.selectedTotal;
    }
    return this.selectedItems.size > 0 ? this.selectedTotal : this.filteredTotal;
  }

  get approvalTotalQty(): number {
    return this.approvalItemsList.reduce((sum, item) => sum + (parseFloat(item.unit) || 0), 0);
  }

  transferToPVLAfterPrinting = true;

  openEstimateApproval() {
    $('#modalEstimateApproval').one('shown.bs.modal', () => this.initSignaturePad());
    $('#modalEstimateApproval').modal('show');
  }

  private initSignaturePad() {
    const pads: SignaturePad[] = [this.signaturePadClient, this.signaturePadAttending].filter(Boolean) as SignaturePad[];
    pads.forEach(pad => {
      pad.resizeCanvas(); // resizes to the now-visible container and clears the pad
    });
  }

  private escapeXml(value: string): string {
    if (value == null) return '';
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  private buildXmlFromPoData(): string {
    const rows = this.approvalItemsList
      .map(r =>
        `<Row proc="${this.escapeXml(r.code_name)}" desc="${this.escapeXml(r.procedure_desc)}" qty="${this.escapeXml((parseFloat(r.unit) || 0).toString())}" price="${this.escapeXml(this.getRowTotalCost(r).toString())}" />`
      )
      .join('');
    return `<Rows>${rows}</Rows>`;
  }

  getClientSignaturePromise(): Promise<number> {
    return this.clientService
      .setupSignatureSolo(0, this.clientService.client.client_no, 2, this.signaturePadClient.toDataURL(), null, '', '')
      .then((client_signature: any) => Number(client_signature));
  }

  getAttendeeSignaturePromise(): Promise<number> {
    return this.clientService
      .setupSignatureSolo(0, this.clientService.userData.id, 2, this.signaturePadAttending.toDataURL(), null, '', '')
      .then((attendee_signature: any) => Number(attendee_signature));
  }

  private async getXmlAndSignatures(): Promise<{ xml: string, client_sig: number, attendee_sig: number }> {
    const xml = this.buildXmlFromPoData();
    const [client_sig, attendee_sig] = await Promise.all([
      this.getClientSignaturePromise(),
      this.getAttendeeSignaturePromise()
    ]);
    return { xml, client_sig, attendee_sig };
  }

  private printReport(xml: string, client_sig: number, attendee_sig: number) {
    let $string = `admin` + "U*63r" + 'FPC_ReleasedEstimate' + "R0d&3" + 'pdf' + "R3nd%" + '' + "Fr3n6" + `FPC_ReleasedEstimate ${new Date().getTime()}` + "T1*73";
    let $result = encode_utf8($string);
    $result = encode_base64($result);
    const xmlB64 = encode_base64(encode_utf8(xml));
    let $resultSafe = encodeURIComponent($result);
    let strfilter = `&clinic_no=${this.petService.userData.clinic_no}&client_no=${this.petService.pet.client_no}&XMLData=${encodeURIComponent(xmlB64)}&client_sig=${client_sig}&attendee_sig=${attendee_sig}&pet_no=${this.petService.pet.pet_no}`;

    let url = `https://ssrs.itimpact.com/fpc_ssrs/default.aspx?token=${$resultSafe}${strfilter}`;

    window.open(url, '_self', 'visible=none');
  }

  private emailReport(xml: string, client_sig: number, attendee_sig: number) {
    const params = {
      report_name: 'FPC_ReleasedEstimate',
      file_name: 'FPC_ReleasedEstimate',
      download: 'true',
      inline: 'true',
      clinic_no: this.petService.userData.clinic_no,
      client_no: this.petService.pet.client_no,
      pet_no: this.petService.pet.pet_no,
      XMLData: encode_base64(encode_utf8(xml)),
      client_sig: client_sig,
      attendee_sig: attendee_sig,
    };

    return this.sharedService.GetReportData(params).then((data) => {
      this.emailService.openEmailModal({
        'from': '',
        'to': [this.clientService.client.email_address],
        'subject': 'Estimate Report for ' + this.petService.pet.pet_name,
        'body': 'Attached is an updated estimate. Please review the findings and respond if required.',
        'attachmentPath': data
      });
    });
  }

  async print() {
    const { xml, client_sig, attendee_sig } = await this.getXmlAndSignatures();
    // transfer first: printReport() navigates the page away via window.open(..., '_self', ...), which would cut off the transfer request
    if (this.transferToPVLAfterPrinting) {
      await this.transferApprovalItemsToPVL();
    }
    this.printReport(xml, client_sig, attendee_sig);
    $('#modalEstimateApproval').modal('hide');
  }

  async emailToClient() {
    const { xml, client_sig, attendee_sig } = await this.getXmlAndSignatures();
    await this.emailReport(xml, client_sig, attendee_sig);
    $('#modalEstimateApproval').modal('hide');
  }

  async printAndEmail() {
    const { xml, client_sig, attendee_sig } = await this.getXmlAndSignatures();
    // email/transfer first: print() navigates the page away via window.open(..., '_self', ...), which would cut off either request
    await this.emailReport(xml, client_sig, attendee_sig);
    if (this.transferToPVLAfterPrinting) {
      await this.transferApprovalItemsToPVL();
    }
    this.printReport(xml, client_sig, attendee_sig);
    $('#modalEstimateApproval').modal('hide');
  }

  constructor(
    public petService: PetService,
    public catalogService: CatalogService,
    public procedureService: ProcedureService,
    public setupService: SetupService,
    public clientService: ClientService,
    public sharedService: SharedService,
    public emailService: EmailService,
    public estimateService: EstimateService,
    private route: ActivatedRoute
  ) {
    this.catalogService.getCategories().then(() => { });
  }

  @ViewChild('formAddProcedures') formAddProcedures: NgForm;

  ngOnInit(): void {
    if (this.pet_no) {
      this.getAllEstProcs();
      this.estimateService.getEstGroupsList(this.petService.pet.pet_no).then(() => { });
    }
    this._subs.add(
      this.procedureService.notify_FromEstModalObservable.subscribe(() => {
        this.getAllEstProcs();
      })
    );

    this._subs.add(
      this.procedureService.selectedFinishGeneric.subscribe((message) => {
        if (!message) { return; }
        this.procedureService.selectedFinishGenericVar(false);
        try {
          if (this.selectedGroup) {
            this.estimateService.getProcsByPetId(
              this.selectedGroup.type === 'G' ? 0 : this.petService.pet.pet_no,
              this.selectedGroup.ID
            ).then(() => {
              this.filteredTable = this.sortByReqDate(this.estimateService.pet_procs);
            });
          } else {
            // refresh after adding a procedure: no blocking wait dialog
            this.getAllEstProcs(false);
          }
        } catch (err) {
          console.error('Error refreshing estimate list after add procedure:', err);
        }
      })
    );
  }

  ngOnDestroy(): void {
    this._subs.unsubscribe();
  }

  search(event: any) {
    setTimeout(() => {

      this.petService.pet_recall.recall_proc1 = event.target.value;

      if (event.key === 'Backspace' || event.key === 'Delete') {
        this.suggestedWordsArray = [];
        this.buscador_activo = false;
      }

      if (this.petService.pet_recall.recall_proc1.length > 1) {
        this.suggestedWordsArray = [];
        this.suggestedWordsArray.push(...this.catalogService.filterArray<CodesFileModel>(this.catalogService.codes_file, this.petService.pet_recall.recall_proc1.toLowerCase().normalize("NFD").replace(/[\u0300\u0301]/g, ""), "code_name"));
        this.suggestedWordsArray.push(...this.catalogService.filterArray<CodesFileModel>(this.catalogService.codes_aliases, this.petService.pet_recall.recall_proc1.toLowerCase().normalize("NFD").replace(/[\u0300\u0301]/g, ""), "procedure_desc"));
        this.buscador_activo = this.suggestedWordsArray.length > 0;
        
      } else {
        this.suggestedWordsArray = [];
        this.buscador_activo = this.suggestedWordsArray.length > 0;
      }
    }, 100);
  }

  //#region Est Table & Groups
  getEstGroupsList() {
    this.estimateService.getEstGroupsList(this.petService.pet.pet_no).then(() => {
      $('#modalEstGroups').modal('show');
    });
  }

  getEstimateItemsBtn(item: EstGroupsListModel, generic: boolean = false) {
    Swal.fire({
      title: 'Loading...',
      timerProgressBar: true,
      didOpen: () => {
        Swal.showLoading();
      },
    });
    let pet_no = generic ? 0 : this.petService.pet.pet_no;
    this.estimateService.getProcsByPetId(pet_no, item.ID).then(() => {
      this.filteredTable = this.sortByReqDate(this.estimateService.pet_procs);
      this.selectedGroup = item;
      this.procedureService.estgroupno = item.ID;
      this.showSelectMode = false;
      this.selectedItems.clear();
      $('#modalEstGroups').modal('hide');
      Swal.close();
    });
  }

  getAllEstProcs(showLoading: boolean = true) {
    if (showLoading) {
      Swal.fire({
        title: 'Loading...',
        timerProgressBar: true,
        didOpen: () => {
          Swal.showLoading();
        },
      });
    }

    this.estimateService.getProcsByPetId(this.petService.pet.pet_no, 0).then(() => {
      this.filteredTable = this.sortByReqDate(this.estimateService.pet_procs);
      this.selectedGroup = null;
      if (showLoading) {
        Swal.close();
      }
    });
  }

  get petEstimates(): EstGroupsListModel[] {
    return (this.estimateService.est_groups_list || []).filter(item => item.type === 'P');
  }

  get genericEstimates(): EstGroupsListModel[] {
    return (this.estimateService.est_groups_list || []).filter(item => item.type === 'G');
  }

  getTooltip(item: PetProcsModel) {
    let tooltip = ``;

    if (item.status === 'D') tooltip += `${item.done_date}`;
    else tooltip += `${item.req_date}`;

    tooltip += ` -- ${item.procedure_desc}`;

    if (item.req_by !== '0' && item.req_by != null && item.req_employee_initial) tooltip += `  ${item.req_employee_initial}`;

    if (item.status === 'T') tooltip += `   Unit: ${item.unit}   Freq.: ${item.done_employee_initial}   Alt Days: ${item.altdays ?? ''}   Start : ${item.done_date}   # Left: ${item.remain_times ?? ''}`;

    if (item.appr_by !== '0' && item.appr_by != null && item.appr_employee_initial) tooltip += `  ${item.appr_employee_initial}`;

    if (item.done_by !== '0' && item.done_by != null && item.done_employee_initial) tooltip += `  ${item.done_employee_initial}`;

    if (item.link_type === INVENT) {
      tooltip += `   Unit: ${item.unit}   Cost: ${this.getRowTotalCost(item)}`;
      if (item.route !== '') tooltip += `   Route: ${item.route}`;
    } else {
      tooltip += `   Unit: ${item.unit}   Cost: ${this.getRowTotalCost(item)}`;
      let strTemp = '';
      if (item.response1 !== '') strTemp += `${item.response1}`;
      if (item.response2 !== '') strTemp += ` / ${item.response2}`;
      if (strTemp !== '') tooltip += `   Resp: ${strTemp}`;
    }

    if (item.location !== '') tooltip += `   Location: ${item.location}`;

    return tooltip;
  }

  getColor(status) {
    if (status == 'R') {
      return this.colors[3]
    }
    else if (status == 'T') {
      return this.colors[2]
    }
    else if (status == 'E') {
      return this.colors[3]
    }
    else if (status == 'T') {
      return this.colors[2]
    }
    else if (status == 'W' || status == 'P') {
      return this.colors[0]
    }
    else {
      return this.colors[4]
    }
  }

  getStatusOption(status) {
    switch (status) {
      case 'R':
        return ['Transfer to PVL', 'Delete']
      case 'D':
        return ['E- ', 'X- Delete']
      case 'S':
        return ['R- Request', 'D- Done', 'T- Treatment', 'X- Delete']
      case 'C':
        return ['R- Request', 'D- Done', 'T- Treatment', 'S - Suggested', 'Q- Quoted', 'X- Delete']
      case 'P':
      case 'W':
        return ['E- ', 'D- Done', 'X- Delete']
      case 'F':
      case 'Q':
      case 'T':
        return ['X- Delete']
    }
  }

  statusChanged(item: PetProcsModel, newStatus: string) {
    if (newStatus === 'Transfer to PVL') {
      console.log("hit")
      this.transferToPVL(item);
    }
    else if (newStatus.includes('Delete')) {
      this.deleteEstProc(item);
    }
  }

  clearSelectedGroup() {
    this.selectedGroup = null;
    this.procedureService.estgroupno = 0;
    this.getAllEstProcs();
  }

  deleteEstProc(item: PetProcsModel) {
    Swal.fire({
      title: 'Are you sure?',
      text: `You want to delete ${item.procedure_desc}`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it!'
    }).then((result) => {
      if (result.isConfirmed) {
        this.estimateService.deleteEstProc(item.tran_sequence).then(() => {
          this.getAllEstProcs();
        });
      }
    });
  }

  //#endregion

  //#region NEW ESTIMATE (with procedure)
  txtchar: any;
  buscador_activo_status: boolean = false;
  filterText: string = '';

  select(item: CodesFileModel) {
    this.filterText = item.code_name;
    this.petService.pet_proc.code_no = item.code_no;
    this.petService.pet_proc.code_type = item.code_type;
    this.petService.pet_proc.code_name = item.code_name;
    this.petService.pet_proc.procedure_desc = item.procedure_desc;
    this.suggestedWordsArray = [];
    this.buscador_activo = this.suggestedWordsArray.length > 0;
    this.buscador_activo_status = true;
    //  debugger;
  }

  resetAddEstimateFields() {
    this.filterText = '';
    this.suggestedWordsArray = [];
    this.buscador_activo = false;
    this.buscador_activo_status = false;
    this.txtchar = '';
    this.petService.pet_proc.code_no = 0;
    this.petService.pet_proc.code_type = 0;
    this.petService.pet_proc.code_name = '';
    this.petService.pet_proc.procedure_desc = '';
  }

  onStatInput() {
    this.txtchar = this.txtchar.toUpperCase();
    this.AddProcedure()
  }

  onStatusBtn(status: string, formAddProcedures: NgForm) {
    this.txtchar = status;
    this.onStatusSelected(null, formAddProcedures);
    setTimeout(() => { this.txtchar = ''; }, 0);
  }

  onStatusSelected(target, formAddProcedures: NgForm) {
    //console.log(target, formAddProcedures);
    this.buscador_activo_status = false;
    if (formAddProcedures.valid) {

      formAddProcedures.ngSubmit.emit();
    }
  }

  async AddProcedure() {
    let code: CodesFileModel;
    this.procedureService.estimation = true;
    switch (this.txtchar) {
      case '*': //AddVaccine
        code = this.catalogService.codes_file.find(t => t.code_name === this.filterText);
        console.log(this.filterText, code);

        if (this.catalogService.clinic_code.some(t => t.pet_info_proc_no === code.code_no && t.pet_info_proc_type === code.code_type)) {

          $('#modalAddProc').modal('hide')
          return;
        }

        let brief_history_category = code.brief_history_category.trim();
        if (brief_history_category === '') {
          // Brief history category not found for this code.
          Swal.fire({
            title: 'Brief history category not found for this code.',
            didOpen: () => {
            },
          })
          return;
        }

        Swal.fire({
          title: 'Loading...',
          timerProgressBar: true,
          didOpen: () => {
            Swal.showLoading()
          },
        })

        this.procedureService.check_pss = new CheckPetSexSpecieModel(code.code_no, code.code_type, this.petService.current_pet_no, INVENT, 1, '', 0);
        this.procedureService.checkPetSexSpecie()
          .then(() => {
            Swal.close();

            if (this.procedureService.check_pss.check === 1) {
              $('#modalAddProc').modal('hide')
              $('#modalAddVaccine').modal('show')
            } else {
              Swal.fire({
                title: this.procedureService.check_pss.message,
                didOpen: () => {
                },
              })
              return;
            }

          });

        break;
      case '$': //PriceLookup
        code = this.catalogService.codes_file.find(t => t.code_name === this.filterText);
        if (code.code_type !== INVENT) {
          //Price lookup feature can only be used on inventory codes.
          Swal.fire({
            title: 'Price lookup feature can only be used on inventory codes.',
            didOpen: () => {
            },
          })
          return;
        }

        let LTrans: TranRecordModel[] = Array(2).fill(new TranRecordModel());
        let intTotal = 1
        let intIndex = -1

        const curUser = this.procedureService.getCurrentUserInfo();
        let result = await this.requestProc(LTrans, intIndex, intTotal, this.filterText, 'R', curUser.initial, curUser.employee_no, this.clientService.current_client_no, this.petService.current_pet_no, 0);
        if (result.RequestProc >= 0) {
          if (LTrans[intIndex].Attending <= 0) {
            //UpdateDocs
          }

          let DoneProc = true; //****
          if (DoneProc) {
            Swal.fire({
              title: `Price Lookup`,
              text: `Price of ${LTrans[intIndex].LinkName} is ${LTrans[intIndex].Cost}`,
              didOpen: () => {
              },
            }).then(() => {
              //DeleteATran
            });
          } else {
            Swal.fire({
              text: `Could not calculate price for ${this.filterText}.`,
              didOpen: () => {
              },
            })
          }
        } else {
          Swal.fire({
            text: `Could not request ${this.filterText} for a price look up.`,
            didOpen: () => {
            },
          })
        }
        break;
      default:
        this.proc();
        break;
    }


  }

  async requestProc(Trans: TranRecordModel[], intIndex: number, intTotalIdx: number, strCodeName: string, strStatus: string, strReqEmp: string, intEmpNo: number,
    lngClientNo: number, lngPetNo: number, lngSupSeq: number, sglPrice?: number, sglLUnit?: number, strLFix?: string, strLChk?: string) {
    return await this.procedureService.requestProc(Trans, intIndex, intTotalIdx, strCodeName, strStatus, strReqEmp, intEmpNo,
      lngClientNo, lngPetNo, lngSupSeq, sglPrice, sglLUnit, strLFix, strLChk);
  }

  async proc() {
    if (this.petService.pet_visit.assigned_doctor && this.petService.pet_visit.assigned_doctor === '') {
      Swal.fire({
        title: 'Please assign attending doctor before requesting items.',
        didOpen: () => {
        },
      })
      return;
    }
    switch (this.txtchar) {
      case 'O':
        // OnlineOrder
        break;
      case 'C':
      case 'D':
      case 'E':
      case 'F':
      case 'P':
      case 'R':
      case 'S':
      case 'T':
      case 'W':
      case 'Q':
        //RequestProc
        // Swal.fire({
        //     title: 'Loading...',
        //     timerProgressBar: true,
        //     didOpen: () => {
        //         Swal.showLoading()
        //     },
        // })

        this.procedureService.strCodeName = this.filterText;
        this.procedureService.txtchar = this.txtchar;
        this.procedureService.lngPetNo = this.petService.pet.pet_no;

        $('#modalAddProc').modal('hide')

        let code = this.catalogService.codes_file.find(t => t.code_name === this.filterText);
        if (code && code.code_type < 0) {
          Swal.fire({
            title: `Code Not Found`,
            text: `Invalid code type for ${this.filterText}`,
            didOpen: () => {
            },
          })
          return;
        }

        let intCodeType = code.code_type;

        if (intCodeType === undefined) {
          Swal.fire({
            title: `Code Not Found`,
            text: `Invalid code type for ${this.filterText}`,
            didOpen: () => {
            },
          })
          return;
        }

        this.procedureService.check_pss = new CheckPetSexSpecieModel(code.code_no, code.code_type, this.petService.current_pet_no, INVENT, 1, '', 0);
        await this.procedureService.checkPetSexSpecie()
          .then(() => {
          });

        if (this.procedureService.check_pss.check === 1) {
        } else {
          Swal.fire({
            title: this.procedureService.check_pss.message,
            didOpen: () => {
            },
          })
          return;
        }

        this.procedureService.selectedGenericVar(true);
        $('#modalAddProcedure').modal('show')

        // var intIdx = await this.requestProc(this.procedureService.displayTran(this.petService.pet_procs), -1, this.petService.pet_procs.length, this.filterText,
        //     this.txtchar, 'ref', 1, this.petService.pet.client_no, this.petService.pet.pet_no, 0);
        // console.log(intIdx);
        // if (intIdx.RequestProc > 0)
        //     this.petService.getProcsByPetId(this.petService.current_pet_no)
        //         .then(() => {
        //             this.filteredTable = this.petService.pet_procs;
        //             Swal.fire({
        //                 title: 'Information stored correctly',
        //             })
        //         });
        return;
      default:
        break;
    }

    $('#modalProcRefer').modal('hide')
  }


  //#endregion

  //#region Modal Add Procedure

  openNewEstGroup() {
    this.estimateService.new_est_group = new EstGroupsListModel(0, this.petService.pet.pet_no, '', '', '', '', '');
    $('#modalNewEstGroup').modal('show');
    $('#modalEstGroups').modal('hide');
  }

  createEstGroup() {
    this.estimateService.new_est_group.pet_no = this.petService.pet.pet_no;
    this.estimateService.new_est_group.created_by = this.petService.userData.employee_name;

    this.estimateService.SetupEstGroup(this.estimateService.new_est_group, 1).then(() => {
      $('#modalNewEstGroup').modal('hide');
      this.estimateService.getEstGroupsList(this.petService.pet.pet_no).then(() => { });
      Swal.fire({
        title: 'Estimate Group Created',
        text: 'The estimate group has been created successfully.',
        icon: 'success',
      });
    });
  }

  openModalAddProcedure() {
    this.procedureService.estimation = true;
    $('#modalAddProcedure').modal('show');
  }

  //#endregion 

  //#region Modal Old Estimates
  openModalOldEstimates() {
    this.petService.getEstimatesByPetId(this.petService.current_pet_no).then(() => {
      $('#modalOldEstimates').modal('show');
    });
  }

  openModalOldEstimateDef(item: EstimateModel) {
    this.petService.pet_estimate = item;

    Swal.fire({
      title: 'Loading...',
      timerProgressBar: true,
      didOpen: () => {
        Swal.showLoading()
      },
    })

    this.petService.getEstimatesDetail(item.estimate_sequence, this.petService.userData.clinic_no).then(() => {
      Swal.close();
      $('#modalOldEstimates').modal('hide');
      $('#modalOldEstimateDef').modal('toggle');
    })
  }

  sortByReqDate(procs: PetProcsModel[]): PetProcsModel[] {
    return [...procs].sort((a, b) => {
      const da = a.req_date ? new Date(a.req_date).getTime() : 0;
      const db = b.req_date ? new Date(b.req_date).getTime() : 0;
      return db - da;
    });
  }

  getRowTotalCost(item: PetProcsModel): number {
    const unit = parseFloat(item.unit) || 0;
    const unitCost = parseFloat(item.unit_cost) || 0;
    return unit * unitCost;
  }

  getTotalCost(): number {
    if (!this.petService.pet_estimate.estimate_def || this.petService.pet_estimate.estimate_def.length === 0) {
      return 0;
    }

    return this.petService.pet_estimate.estimate_def
      .filter(item => item.link_count !== 0)
      .reduce((total, item) => total + (item.link_cost || 0), 0);
  }

  async applyOldEstDef() {
    const defs = this.petService.pet_estimate?.estimate_def || [];

    this.estimateService.new_est_group.description = this.petService.pet_estimate.estimate_name;
    this.estimateService.new_est_group.type = 'P';
    this.estimateService.new_est_group.remarks = "Old Estimate with ID:" + this.petService.pet_estimate.estimate_sequence;
    this.estimateService.new_est_group.created_by = this.petService.userData.employee_name;
    this.estimateService.new_est_group.pet_no = this.petService.pet.pet_no;
    await this.estimateService.SetupEstGroup(this.estimateService.new_est_group, 1).then((res: number) => {
      if (res > 0) {
        this.procedureService.estgroupno = res;
        this.estimateService.getEstGroupsList(this.petService.pet.pet_no).then(() => { });
      }
      else {
        Swal.fire({
          title: 'Error',
          text: 'Failed to create estimate group',
        })
        return;
      }
    })

    // helper: wait until any of the known modals is visible, then wait until it's closed
    const waitForModalInteraction = async () => {
      const modalIds = ['#modalAddProcedure', '#modalAddVaccine', '#modalAddProc', '#modalProcRefer'];
      // wait up to 2s for a modal to appear
      const appeared = await new Promise<string | null>(resolve => {
        const maxWait = 2000;
        const interval = 100;
        let waited = 0;
        const iv = setInterval(() => {
          for (const m of modalIds) {
            if ($(m).is(':visible')) {
              clearInterval(iv);
              resolve(m);
              return;
            }
          }
          waited += interval;
          if (waited >= maxWait) {
            clearInterval(iv);
            resolve(null);
          }
        }, interval);
      });

      if (!appeared) {
        // no modal opened — nothing to wait for
        return;
      }

      // wait for that modal to be hidden (user completes interaction)
      await new Promise<void>(resolve => {
        $(appeared).one('hidden.bs.modal', () => resolve());
      });
      // small delay to allow UI to stabilize
      await new Promise(res => setTimeout(res, 100));
    };

    (async () => {
      for (const item of defs) {
        if (!item || item.link_count === 0) { continue; }

        // populate fields AddProcedure relies on
        this.filterText = item.code_name || '';
        this.txtchar = 'R';

        try {
          // call AddProcedure (it will open a modal for user input)
          this.AddProcedure();
          // wait for the modal interaction to complete before proceeding to next item
          await waitForModalInteraction();
        } catch (err) {
          console.error('applyOldEstDef: AddProcedure failed for', item, err);
        }
      }

      // close modal after applying
      $('#modalOldEstimateDef').modal('hide');
    })();
  }
  //#endregion

  //#region PVL Connection
  @Output() onTransferToPVL = new EventEmitter<PetProcsModel[]>();

  // Transfers the printed items to PVL (brief success toast instead of the heavier
  // loading/"Go to PVL" dialog), using the same group-type branching as
  // transferToPVL/transferAllToPVL. Called from print()/printAndEmail() right before
  // printReport() navigates the tab away via window.open(..., '_self', ...), which would
  // otherwise cut the transfer request off (see the comment in printAndEmail()).
  //
  // Always emits onTransferToPVL with the transferred items afterward - that's what tells
  // landing-page.component.ts (ver('procedures') + notify_FromEstModalFunction) to refresh
  // the PVL view with the newly added items. The 'P'/no-group branch used to skip this emit
  // entirely, which is why PVL needed a manual refresh to show items transferred this way.
  private async transferApprovalItemsToPVL(): Promise<void> {
    const items = this.approvalItemsList;
    if (!items || items.length === 0) return;

    if (!this.selectedGroup || this.selectedGroup.type === 'P') {
      await this.procedureService.TransferEstTotblTransaction(items);
      // already transferred in the DB: emit an empty list so landing-page switches to PVL
      // and just reloads its procedure list. Emitting the items would make PVL re-run its
      // add-procedure flow for each of them.
      this.onTransferToPVL.emit([]);
    } else {
      this.onTransferToPVL.emit(items);
    }

    await Swal.fire({
      title: 'Successfully Transferred to PVL',
      icon: 'success',
      timer: 1500,
      showConfirmButton: false,
    });
  }

  transferToPVL(petProcsModel: PetProcsModel) {
    if (!this.selectedGroup) {
      this.TransferEstTotblTransaction([petProcsModel]);
    }
    else if (this.selectedGroup && this.selectedGroup.type === 'P') {
      this.TransferEstTotblTransaction([petProcsModel]);
    }
    else {
      // this.filteredTable.forEach(item => item.status = 'C');
      this.onTransferToPVL.emit([petProcsModel]);
    }
  }

  transferAllToPVL() {
    if (this.selectedGroup && this.selectedGroup.type === 'P') {
      this.TransferEstTotblTransaction(this.filteredTable);
    }
    else if (this.selectedGroup && this.selectedGroup.type === 'G') {
      if (this.filteredTable && this.filteredTable.length > 0) {
        this.onTransferToPVL.emit(this.filteredTable);
      }
    }
    else {
      console.log("WHAT")
    }
  }

  TransferEstTotblTransaction(petProcsModel: PetProcsModel[]) {
    Swal.fire({
      title: 'Loading...',
      timerProgressBar: true,
      didOpen: () => {
        Swal.showLoading()
      },
    })

    this.procedureService.TransferEstTotblTransaction(petProcsModel).then(() => {
      // refresh PVL right away, whether or not the user chooses "Go to PVL" below
      this.procedureService.notify_InitProcTableModalFunction();
      Swal.close();
      Swal.fire({
        title: 'Successfully Transferred to PVL',
        icon: 'success',
        showCancelButton: true,
        confirmButtonText: 'Go to PVL',
        cancelButtonText: 'Close'
      }).then((result) => {
        if (result.isConfirmed) {
          this.onTransferToPVL.emit([]);
        }
      });
    });
  }
  //#endregion
}
